import React, { useState, useRef, useEffect } from "react";
import { message, Spin } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { processPatientOrdersApi } from "../../../services/doctor.services";
import AiVoiceRecorder from "./AiVoiceRecorder";
import "../MedicationsIp/AiMedications/VoiceRecorder.css";

export default function FloatingRecorder({
  onProcessComplete,
  onClose,
  userDetails,
}) {
  const [recordedFile, setRecordedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoStart, setAutoStart] = useState(true); // 🔥 NEW: Auto-start state
  const voiceRecorderRef = useRef(null); // 🔥 Reference to the recorder

  // 🔥 Cleanup when component unmounts or closes
  useEffect(() => {
    return () => {
      if (voiceRecorderRef.current) {
        voiceRecorderRef.current.stopRecordingAndCleanup();
      }
    };
  }, []);
  useEffect(() => {
    // Since we use key=... in the parent, this component is new.
    // We set autoStart to false after the first render to prevent re-triggering.
    // AiVoiceRecorder will read this state on its first render.
    setAutoStart(false);
  }, []);

  const onRecordSubmit = async () => {
    if (!recordedFile) {
      message.error("No recording found");
      return;
    }

    setLoading(true);
    // CRITICAL: Stop the recorder and tracks *before* making the API call.
    // This ensures the red dot goes away before waiting for the network response.
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.stopRecordingAndCleanup();
    }
    // ... rest of the submit logic ...

    try {
      const fd = new FormData();
      fd.append("audio_file", recordedFile);

      const response = await processPatientOrdersApi(fd);
      message.success("Audio processed successfully!");

      onProcessComplete(response?.data || {});
    } catch (error) {
      console.error(error);
      message.error("Failed to process audio.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // 🔥 Stop recording and cleanup before closing (Kept As Is)
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.stopRecordingAndCleanup();
    }
    onClose();
  };

  return (
    <>
      {/* Semi-transparent Backdrop - NO pointer-events blocking */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          // backgroundColor: "rgba(0, 0, 0, 0.25)",
          zIndex: 1000,
          pointerEvents: "none",
        }}
      />

      {/* Floating Recorder Card — stacks above AI float (bottom padding nests the FAB) */}
      <div
        className="ai-floating-recorder"
        style={{
          borderColor:
            userDetails?.activeOrg?.color ||
            "var(--ant-primary-color, #7F56D9)",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Spin size="large" tip="Processing..." />
          </div>
        ) : (
          <>
            {/* Close Button */}
            <div
              className="ai-floating-recorder__close"
              onClick={handleClose}
              role="button"
              tabIndex={0}
              aria-label="Close recorder"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleClose();
              }}
            >
              <CloseOutlined style={{ fontSize: "18px", color: "#fff" }} />
            </div>

            {/* Middle slot: mic pause / submit — reserved height so controls never collide with AI FAB */}
            <div className="ai-floating-recorder__controls">
              <AiVoiceRecorder
                ref={voiceRecorderRef}
                recordedFile={recordedFile}
                startRecord={autoStart}
                setStartRecord={setAutoStart}
                onRecordSubmit={onRecordSubmit}
                setRecordedFile={setRecordedFile}
                isFloating={true}
              />

              {recordedFile && (
                <div
                  className="ai-floating-recorder__submit"
                  onClick={onRecordSubmit}
                  role="button"
                  tabIndex={0}
                  aria-label="Submit recording"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onRecordSubmit();
                  }}
                >
                  <CheckOutlined style={{ fontSize: "18px", color: "#fff" }} />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
